/**
 * TadilatTalepPublicForm.jsx — Bölüm 25-B
 *
 * Token tabanlı public form — login gerekmez.
 * URL: /tadilat-form?token=...
 *
 * Adımlar:
 *  1) Token parametresini al → getFairData() çağır
 *  2) Fuar/Salon/Stand dropdown'larını doldur
 *  3) Form doldur → submit
 *  4) Başarı ekranı göster
 */
import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Container, Card, CardBody, CardHeader,
  Row, Col, Button, Input, Spinner, Alert,
  Form, FormGroup, Label, Badge,
} from "reactstrap";
import { Plus, Trash2, Send, CheckCircle } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { getFairData, submitTadilatTalep } from "../../../services/tadilaTalepService";
import { useTranslation } from "react-i18next";
import LanguageDropdown from "../../../Components/Common/LanguageDropdown";
import { LanguageContext } from "../../../Components/Contexts/LanguageContext";
import { STORAGE_KEY } from "../../../i18n";

// ─── Boş personel kaydı ───────────────────────────────────────────────────────
const emptyPersonel = () => ({ ad: "", soyad: "", tckn: "" });

// ─── Boş form state ───────────────────────────────────────────────────────────
const emptyForm = () => ({
  salonId:      "",
  standId:      "",

  katilimciFirmaAdi: "",
  talepKisi: { adSoyad: "", telefon: "" },

  dekorator: { firmaAdi: "", yetkiliAdSoyad: "", yetkiliTelefon: "" },

  tadilat: { yapilacakIs: "", tahminiSure: "" },

  personeller: [emptyPersonel()],
});

// ─── Input helper ─────────────────────────────────────────────────────────────
function FieldGroup({ label, required, children }) {
  return (
    <FormGroup>
      <Label className="fw-semibold small">
        {label}{required && <span className="text-danger ms-1">*</span>}
      </Label>
      {children}
    </FormGroup>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const TadilatTalepPublicForm = () => {
  const { t } = useTranslation("tadilat");
  // #335: no header on this public page, so it carries its own TR/EN switch. A representative
  // opening the link for the first time (nothing stored) gets the browser's language: English
  // unless the browser is Turkish. Afterwards their own choice is remembered.
  const { selectedLang, setSelectedLang } = useContext(LanguageContext);
  useEffect(() => {
    let stored = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch (e) { /* private mode */ }
    if (!stored) setSelectedLang(/^tr\b/i.test(navigator.language || "") ? "tr" : "en");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dilSecici = (
    <div className="position-absolute top-0 end-0 p-2">
      <LanguageDropdown selectedLang={selectedLang} setSelectedLang={setSelectedLang} />
    </div>
  );
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [fairData,    setFairData]    = useState(null);   // { fuarAdi, salonlar }
  const [tokenError,  setTokenError]  = useState(null);
  const [tokenLoading,setTokenLoading]= useState(true);

  const [form, setForm]         = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted,   setSubmitted]   = useState(false);

  // Token doğrula + fair data yükle
  // inner async fn kullanılıyor — effect body'de doğrudan setState yok
  useEffect(() => {
    let cancelled = false;

    async function loadFairData() {
      if (!token) {
        // token yoksa hata göster, loading kapat — inner fn içinde
        setTokenError("form.gecersizVeyaEksikTokenLutfen"); // #335: the key; translated at render
        setTokenLoading(false);
        return;
      }
      try {
        const data = await getFairData(token);
        // API Guide v2 (Bölüm 3.3) format:
        // { fuarId, fuarAdi, salonlar: [{salonId, salonAdi}], standlar: [{standId, standNo, salonId}] }
        // Normalize edilmiş format tadilaTalepService.js tarafından döndürülüyor.
        console.log('[TadilatTalepPublicForm] getFairData response:', data);
        if (!cancelled) setFairData(data);
      } catch (e) {
        console.error('[TadilatTalepPublicForm] getFairData error:', e);
        if (!cancelled) {
          if (e?.response?.status === 404 || e?.response?.status === 401) {
            setTokenError("form.buLinkGecersizVeyaSuresi"); // #335: the key; translated at render
          } else {
            setTokenError("form.baglantiHatasiLutfenDahaSonra"); // #335: the key; translated at render
          }
        }
      } finally {
        if (!cancelled) setTokenLoading(false);
      }
    }

    loadFairData();
    return () => { cancelled = true; };
  }, [token]);

  // Seçili salona ait standları filtrele
  // API Guide: standlar flat array üst seviyede, salonId ile filtreleniyor
  const standlar = form.salonId
    ? (fairData?.standlar ?? []).filter((s) => String(s.salonId) === String(form.salonId))
    : [];

  // ─── Form helpers ─────────────────────────────────────────────────────────
  const setField = useCallback((path, value) => {
    setForm((prev) => {
      const parts = path.split(".");
      if (parts.length === 1) return { ...prev, [parts[0]]: value };
      if (parts.length === 2)
        return { ...prev, [parts[0]]: { ...prev[parts[0]], [parts[1]]: value } };
      return prev;
    });
  }, []);

  const setPersonelField = (idx, key, value) => {
    setForm((prev) => {
      const updated = prev.personeller.map((p, i) =>
        i === idx ? { ...p, [key]: value } : p
      );
      return { ...prev, personeller: updated };
    });
  };

  const addPersonel = () =>
    setForm((prev) => ({ ...prev, personeller: [...prev.personeller, emptyPersonel()] }));

  const removePersonel = (idx) =>
    setForm((prev) => ({
      ...prev,
      personeller: prev.personeller.filter((_, i) => i !== idx),
    }));

  // ─── Validation ──────────────────────────────────────────────────────────
  function validate() {
    if (!form.salonId || !form.standId) return t("form.salonVeStandSeciniz");
    if (!form.katilimciFirmaAdi.trim())     return t("form.katilimciFirmaAdiZorunludur");
    if (!form.talepKisi.adSoyad.trim())     return t("form.talepEdenKisininAdiZorunludur");
    if (!form.talepKisi.telefon.trim())     return t("form.talepEdenKisininTelefonuZorunludur");
    if (!form.tadilat.yapilacakIs.trim())   return t("form.yapilacakIsAciklamasiZorunludur");
    for (const [i, p] of form.personeller.entries()) {
      if (!p.ad.trim() || !p.soyad.trim() || !p.tckn.trim())
        return `Personel ${i + 1}: ad, soyad ve TCKN zorunludur.`;
      if (p.tckn.replace(/\D/g, "").length !== 11)
        return t("form.personelTckn11HaneliOlmalidir", { v: i + 1 });
    }
    return null;
  }

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setSubmitError(err); return; }
    setSubmitError(null);
    setSubmitting(true);
    try {
      // API Guide v2 (Bölüm 3.3) — POST /public/tadilat/submit
      // Zorunlu: token, standId, talepKisi.adSoyad + telefon, tadilat.yapilacakIs, personeller
      // salonId opsiyonel; fuarId ve katilimciFirmaAdi backend'de token'dan çıkarılır
      const body = {
        token,
        salonId: form.salonId ? Number(form.salonId) : null,
        standId: Number(form.standId),
        talepKisi: {
          adSoyad: form.talepKisi.adSoyad.trim(),
          telefon: form.talepKisi.telefon.trim(),
        },
        dekorator: {
          firmaAdi:           form.dekorator.firmaAdi.trim(),
          yetkiliAdSoyad:     form.dekorator.yetkiliAdSoyad.trim(),
          yetkiliTelefon:     form.dekorator.yetkiliTelefon.trim(),
        },
        tadilat: {
          yapilacakIs:   form.tadilat.yapilacakIs.trim(),
          tahminiSure:   form.tadilat.tahminiSure.trim(),
        },
        personeller: form.personeller.map((p) => ({
          ad:   p.ad.trim(),
          soyad: p.soyad.trim(),
          tckn:  p.tckn.replace(/\D/g, ""),
        })),
      };
      console.log('[TadilatTalepPublicForm] submit body:', body);
      await submitTadilatTalep(body);
      setSubmitted(true);
    } catch (err) {
      console.error('[TadilatTalepPublicForm] submit error:', err?.response?.data ?? err);
      setSubmitError(t("form.basvuruGonderilirkenHataOlustuLutfen"));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render states ────────────────────────────────────────────────────────
  if (tokenLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light position-relative">
        {dilSecici}
        <div className="text-center">
          <Spinner color="primary" style={{ width: 40, height: 40 }} />
          <p className="mt-3 text-muted">{t("form.baglantiDogrulaniyor")}</p>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light position-relative">
        {dilSecici}
        <Container style={{ maxWidth: 480 }}>
          <Card className="shadow border-0">
            <CardBody className="text-center py-5">
              <div className="text-danger mb-3" style={{ fontSize: 48 }}>⛔</div>
              <h5 className="fw-bold text-danger">{t("form.gecersizLink")}</h5>
              <p className="text-muted">{t(tokenError)}</p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light position-relative">
        {dilSecici}
        <Container style={{ maxWidth: 480 }}>
          <Card className="shadow border-0">
            <CardBody className="text-center py-5">
              <CheckCircle size={56} className="text-success mb-3" />
              <h5 className="fw-bold text-success">{t("form.basvurunuzAlindi")}</h5>
              <p className="text-muted">
                {t("form.tadilatTalepBasvurunuzBasariylaIletildi")}
              </p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  // ─── Form Render ──────────────────────────────────────────────────────────
  return (
    <div className="min-vh-100 bg-light py-4 position-relative">
      {dilSecici}
      <Container style={{ maxWidth: 720 }}>
        {/* Başlık */}
        <div className="text-center mb-4">
          <h4 className="fw-bold">{t("form.tadilatTalepFormu")}</h4>
          {fairData?.fuarAdi && (
            <Badge color="primary" className="px-3 py-2 rounded-pill fs-6">
              {fairData.fuarAdi}
            </Badge>
          )}
        </div>

        <Form onSubmit={handleSubmit}>
          {/* BÖLÜM 1: Fuar & Stand */}
          <Card className="shadow-sm border-0 mb-3">
            <CardHeader className="fw-semibold bg-primary text-white">
              {t("form.n1StandBilgileri")}
            </CardHeader>
            <CardBody>
              <Row className="g-3">
                <Col md={6}>
                   <FieldGroup label={t("form.salon")} required>
                    <Input
                      type="select"
                      value={form.salonId}
                      onChange={(e) => {
                        setField("salonId", e.target.value);
                        setField("standId", "");
                      }}
                    >
                      <option value="">{t("form.salonSecin")}</option>
                      {/* API Guide: salonlar → [{ salonId, salonAdi }] */}
                      {(fairData?.salonlar ?? []).map((s) => (
                        <option key={s.salonId} value={s.salonId}>{s.salonAdi}</option>
                      ))}
                    </Input>
                  </FieldGroup>
                </Col>
                <Col md={6}>
                  <FieldGroup label={t("form.standNo")} required>
                    <Input
                      type="select"
                      value={form.standId}
                      onChange={(e) => setField("standId", e.target.value)}
                      disabled={!form.salonId}
                    >
                      <option value="">{t("form.standSecin")}</option>
                      {/* API Guide: standlar → flat array, salonId ile üstte filtrelendi */}
                      {standlar.map((s) => (
                        <option key={s.standId} value={s.standId}>{s.standNo}</option>
                      ))}
                    </Input>
                  </FieldGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* BÖLÜM 2: Katılımcı Firma / Talep Eden */}
          <Card className="shadow-sm border-0 mb-3">
            <CardHeader className="fw-semibold bg-primary text-white">
              {t("form.n2KatilimciFirmaTalepEden")}
            </CardHeader>
            <CardBody>
              <Row className="g-3">
                <Col md={12}>
                  <FieldGroup label={t("form.katilimciFirmaAdi")} required>
                    <Input
                      placeholder={t("form.firmaAdi")}
                      value={form.katilimciFirmaAdi}
                      onChange={(e) => setField("katilimciFirmaAdi", e.target.value)}
                    />
                  </FieldGroup>
                </Col>
                <Col md={6}>
                  <FieldGroup label={t("form.adSoyad")} required>
                    <Input
                      placeholder={t("form.talepEdenKisininAdi")}
                      value={form.talepKisi.adSoyad}
                      onChange={(e) => setField("talepKisi.adSoyad", e.target.value)}
                    />
                  </FieldGroup>
                </Col>
                <Col md={6}>
                  <FieldGroup label={t("form.telefon")} required>
                    <Input
                      placeholder="05xx xxx xx xx"
                      value={form.talepKisi.telefon}
                      onChange={(e) => setField("talepKisi.telefon", e.target.value)}
                    />
                  </FieldGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* BÖLÜM 3: Dekoratör */}
          <Card className="shadow-sm border-0 mb-3">
            <CardHeader className="fw-semibold bg-secondary text-white">
              {t("form.n3DekoratorBilgileri")}
            </CardHeader>
            <CardBody>
              <Row className="g-3">
                <Col md={12}>
                  <FieldGroup label={t("form.dekoratorFirmaAdi")}>
                    <Input
                      placeholder={t("form.dekoratorFirmaAdi2")}
                      value={form.dekorator.firmaAdi}
                      onChange={(e) => setField("dekorator.firmaAdi", e.target.value)}
                    />
                  </FieldGroup>
                </Col>
                <Col md={6}>
                  <FieldGroup label={t("form.yetkiliAdSoyad")}>
                    <Input
                      placeholder={t("form.yetkiliKisininAdi")}
                      value={form.dekorator.yetkiliAdSoyad}
                      onChange={(e) => setField("dekorator.yetkiliAdSoyad", e.target.value)}
                    />
                  </FieldGroup>
                </Col>
                <Col md={6}>
                  <FieldGroup label={t("form.yetkiliTelefonu")}>
                    <Input
                      placeholder="05xx xxx xx xx"
                      value={form.dekorator.yetkiliTelefon}
                      onChange={(e) => setField("dekorator.yetkiliTelefon", e.target.value)}
                    />
                  </FieldGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* BÖLÜM 4: Tadilat Bilgileri */}
          <Card className="shadow-sm border-0 mb-3">
            <CardHeader className="fw-semibold bg-primary text-white">
              {t("form.n4TadilatBilgileri")}
            </CardHeader>
            <CardBody>
              <Row className="g-3">
                <Col md={12}>
                  <FieldGroup label={t("form.yapilacakIs")} required>
                    <Input
                      type="textarea"
                      rows={4}
                      placeholder={t("form.tadilatKapsamindakiIsleriAciklayin")}
                      value={form.tadilat.yapilacakIs}
                      onChange={(e) => setField("tadilat.yapilacakIs", e.target.value)}
                    />
                  </FieldGroup>
                </Col>
                <Col md={6}>
                  <FieldGroup label={t("form.tahminiSure")}>
                    <Input
                      placeholder={t("form.orn2Gun")}
                      value={form.tadilat.tahminiSure}
                      onChange={(e) => setField("tadilat.tahminiSure", e.target.value)}
                    />
                  </FieldGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* BÖLÜM 5: Personel Listesi */}
          <Card className="shadow-sm border-0 mb-4">
            <CardHeader className="fw-semibold bg-primary text-white d-flex justify-content-between align-items-center">
              <span>{t("form.n5CalisacakPersoneller")}</span>
              <Button color="light" size="sm" type="button" onClick={addPersonel} className="d-flex align-items-center gap-1">
                <Plus size={13} />{" "}{t("form.personelEkle")}
              </Button>
            </CardHeader>
            <CardBody>
              {form.personeller.map((p, idx) => (
                <div key={idx} className="border rounded p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small fw-semibold text-muted">{t("form.personel")}{" "}{idx + 1}</span>
                    {form.personeller.length > 1 && (
                      <Button type="button" color="danger" size="sm" outline onClick={() => removePersonel(idx)} className="p-1 lh-1">
                        <Trash2 size={13} />
                      </Button>
                    )}
                  </div>
                  <Row className="g-2">
                    <Col md={4}>
                      <Input
                        placeholder={t("form.ad")}
                        value={p.ad}
                        onChange={(e) => setPersonelField(idx, "ad", e.target.value)}
                      />
                    </Col>
                    <Col md={4}>
                      <Input
                        placeholder={t("form.soyad")}
                        value={p.soyad}
                        onChange={(e) => setPersonelField(idx, "soyad", e.target.value)}
                      />
                    </Col>
                    <Col md={4}>
                      <Input
                        placeholder={t("form.tckn")}
                        maxLength={11}
                        value={p.tckn}
                        onChange={(e) => setPersonelField(idx, "tckn", e.target.value.replace(/\D/g, ""))}
                      />
                    </Col>
                  </Row>
                </div>
              ))}
            </CardBody>
          </Card>

          {/* Hata */}
          {submitError && (
            <Alert color="danger" className="mb-3">
              {submitError}
            </Alert>
          )}

          {/* Submit */}
          <div className="text-end mb-5">
            <Button
              type="submit"
              color="success"
              size="lg"
              disabled={submitting}
              className="d-inline-flex align-items-center gap-2 px-5"
            >
              {submitting ? (
                <><Spinner size="sm" />{" "}{t("form.gonderiliyor")}</>
              ) : (
                <><Send size={16} />{" "}{t("form.basvuruyuGonder")}</>
              )}
            </Button>
          </div>
        </Form>
      </Container>
    </div>
  );
};

export default TadilatTalepPublicForm;



